import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, RefreshCw, Search, Send, X } from 'lucide-react';
import { db } from '@/lib/db';
import { notifyFollowingChanged } from '@/lib/following-events';
import type { Invitation, MemberPublic } from '@/lib/db/types';
import { timeAgo } from '@/lib/timeAgo';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  onClose: () => void;
  onOpenProfile: (id: string) => void;
  onInvite: () => void;
}

function fmtInviteContact(inv: Invitation): string {
  if (inv.recipient_name) return inv.recipient_name;
  if (inv.phone) return inv.phone;
  return inv.email || 'Invite';
}

export function MembersSheet({ onClose, onOpenProfile, onInvite }: Props) {
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState<MemberPublic[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [following, setFollowing] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [people, ids, invs] = await Promise.all([
      db.members.list({ limit: 200 }),
      db.follows.following(),
      db.invitations.list(),
    ]);
    setMembers(people);
    setFollowing(ids);
    setInvitations(invs);
  };

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) => m.name.toLowerCase().includes(q) || m.handle.toLowerCase().includes(q),
    );
  }, [members, search]);

  const pending = invitations.filter((i) => i.status === 'pending');

  const toggle = async (id: string) => {
    const wasFollowing = following.includes(id);
    if (wasFollowing) {
      await db.follows.remove(id);
      setFollowing((prev) => prev.filter((x) => x !== id));
      notifyFollowingChanged(id, false);
    } else {
      await db.follows.add(id);
      setFollowing((prev) => (prev.includes(id) ? prev : [...prev, id]));
      notifyFollowingChanged(id, true);
    }
  };

  const cancelInvite = async (id: string) => {
    await db.invitations.cancel(id);
    setInvitations((prev) => prev.map((i) => (i.id === id ? { ...i, status: 'cancelled' as const } : i)));
  };

  const resendInvite = async (id: string) => {
    await db.invitations.resend(id);
    await load();
  };

  return (
    <Sheet title="Members" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <button
          type="button"
          onClick={onInvite}
          className="mb-4 flex w-full items-center gap-3 rounded border border-gold-deep bg-navy p-3.5 text-left text-cream"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold text-navy">
            <Plus size={18} strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-[15px] font-bold">Invite a member</div>
            <div className="mt-0.5 text-[11px] text-gold-bright">Membership is invite-only · Email a friend</div>
          </div>
          <ChevronRight size={18} className="shrink-0 text-gold" />
        </button>

        <div className="relative mb-4">
          <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members…"
            className="w-full rounded-[10px] border border-line bg-cream py-3 pl-10 pr-3.5 text-[15px] text-ink outline-none"
          />
        </div>

        {pending.length > 0 && !search.trim() && (
          <>
            <div className="mb-2 flex items-center gap-1.5 font-serif text-xs font-bold tracking-[0.15em] text-gold-deep">
              <Send size={12} />
              {pending.length} PENDING INVITATION{pending.length === 1 ? '' : 'S'}
            </div>
            {pending.map((inv) => (
              <div
                key={inv.id}
                className="mb-1.5 flex items-center gap-3 rounded border border-dashed border-gold bg-gold/[0.06] px-3.5 py-3"
              >
                <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-gold bg-paper text-base text-gold-deep">
                  ✉️
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-serif text-sm font-bold text-ink">{fmtInviteContact(inv)}</div>
                  <div className="mt-0.5 text-[10px] text-mute">
                    {inv.recipient_name && (inv.email || inv.phone) ? `${inv.email || inv.phone} · ` : ''}
                    Sent {timeAgo(inv.created_at)} ago
                  </div>
                </div>
                <button type="button" onClick={() => void resendInvite(inv.id)} className="p-1.5 text-gold-deep">
                  <RefreshCw size={14} />
                </button>
                <button type="button" onClick={() => void cancelInvite(inv.id)} className="p-1.5 text-bad">
                  <X size={16} />
                </button>
              </div>
            ))}
            <div className="mb-4 h-4" />
          </>
        )}

        <div className="mb-2 font-serif text-xs font-bold tracking-[0.15em] text-mute">
          {loading ? '…' : filtered.length} ACTIVE MEMBER{filtered.length === 1 ? '' : 'S'}
        </div>

        {loading && <div className="text-sm text-mute">Loading…</div>}

        {filtered.map((m) => (
          <div
            key={m.id}
            className="mb-1.5 flex items-center gap-3 rounded border border-line bg-paper px-3 py-3.5"
          >
            <button
              type="button"
              onClick={() => { onOpenProfile(m.id); onClose(); }}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <Avatar member={m} size={42} />
              <div className="min-w-0 flex-1">
                <div className="font-serif text-sm font-bold text-ink">{m.name}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-mute">
                  @{m.handle} · Hcp {m.handicap ?? '—'}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => void toggle(m.id)}
              className={`rounded px-3 py-1.5 text-[11px] font-bold tracking-wide ${
                following.includes(m.id)
                  ? 'border border-navy bg-transparent text-navy'
                  : 'border border-navy bg-navy text-cream'
              }`}
            >
              {following.includes(m.id) ? 'FOLLOWING' : 'FOLLOW'}
            </button>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
